import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, MapPin, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { PrimaryButton } from "@/components/kit";
import { Field, inputClass } from "@/components/AuthShell";

const CITIES = ["Portland", "Eugene-Springfield", "San Francisco"];

export default function Waitlist() {
  const [searchParams] = useSearchParams();
  const source = searchParams.get("ref") || searchParams.get("src") || "";
  const [email, setEmail] = useState("");
  const [city, setCity] = useState(CITIES[0]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiFetch("/waitlist", { method: "POST", body: JSON.stringify({ email: email.trim(), city, source }) }, false);
      setDone(true);
    } catch (err) {
      toast.error(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FCFBF8] text-[#2D2B2A]">
      <header className="max-w-4xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <Logo size={38} to="/" />
        <Link to="/login" className="px-4 py-2 rounded-full font-bold text-[#4A4746] hover:bg-[#F2EFE9]">Log in</Link>
      </header>

      <section className="max-w-xl mx-auto px-5 sm:px-8 pt-8 pb-16">
        {done ? (
          <div className="text-center py-10" data-testid="waitlist-success">
            <div className="h-20 w-20 rounded-3xl bg-[#E8F0E5] flex items-center justify-center mx-auto mb-5">
              <PartyPopper size={38} color="#6F8F72" />
            </div>
            <h1 className="font-display text-4xl font-black">You're on the list! 🎉</h1>
            <p className="text-[#4A4746] mt-3 text-lg">
              We're rolling out Wooflect city by city. We'll email <b>{email}</b> the moment beta opens in <b>{city}</b>.
            </p>
            <Link to="/" className="inline-block mt-7">
              <PrimaryButton className="!px-8">Back to home</PrimaryButton>
            </Link>
          </div>
        ) : (
          <>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F6DED5] text-[#B54828] text-xs font-extrabold uppercase tracking-wide">
              Private beta
            </span>
            <h1 className="font-display text-5xl font-black leading-[1.05] mt-5">Join the Wooflect beta.</h1>
            <p className="text-lg text-[#4A4746] mt-4">
              We're launching in a few cities first. Grab your spot and be among the first dog parents to get AI health guidance + trusted local vets.
            </p>

            <form onSubmit={submit} className="mt-8 bg-white rounded-3xl border border-[#E8E3DA] p-6">
              <Field label="Email">
                <input
                  data-testid="waitlist-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className={inputClass}
                />
              </Field>
              <Field label="Your city">
                <div className="grid sm:grid-cols-3 gap-2">
                  {CITIES.map((c) => (
                    <button
                      type="button"
                      key={c}
                      data-testid={`waitlist-city-${c}`}
                      onClick={() => setCity(c)}
                      className={`flex items-center justify-center gap-1.5 px-3 py-3 rounded-2xl border-2 text-sm font-bold transition-all ${
                        city === c ? "border-[#E86A45] bg-[#FFF7F3] text-[#B54828]" : "border-[#E8E3DA] text-[#4A4746] hover:border-[#C4BFB8]"
                      }`}
                    >
                      <MapPin size={15} /> {c}
                    </button>
                  ))}
                </div>
              </Field>
              {source && <p className="text-xs text-[#9A938B] mb-3">Referred via <b>{source}</b></p>}
              <PrimaryButton data-testid="waitlist-submit-btn" type="submit" loading={loading} className="w-full">
                Reserve my spot
              </PrimaryButton>
              <div className="flex items-center gap-4 mt-4 text-sm text-[#4A4746] justify-center">
                <span className="flex items-center gap-1.5"><Check size={15} color="#6F8F72" /> Free to join</span>
                <span className="flex items-center gap-1.5"><Check size={15} color="#6F8F72" /> No spam</span>
              </div>
            </form>
          </>
        )}
      </section>
    </div>
  );
}
